// pages/ReportsPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    FileText, Calendar, RefreshCw, Package, Truck, DollarSign,
    CheckCircle, AlertTriangle, CalendarDays, X, Filter, Hash,
    Store, Ship, BarChart3, Clock, MapPin, User,
    Star, Target, Fuel, Users, Eye, Flag, Activity, Send, Home,
    Download, FileSpreadsheet, Navigation
} from 'lucide-react';
import { Card, Button, Badge, Input } from '../components/ui';
import { API_BASE_URL, API_ENDPOINTS } from "../constants";
import * as XLSX from 'xlsx';
import { RouteAnalyticsTab } from '../components/analytics';

const ReportsPage = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('overview');
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [dateRange, setDateRange] = useState({
        start: new Date().toISOString().split('T')[0],
        end: new Date().toISOString().split('T')[0]
    });
    const [reportData, setReportData] = useState({});
    const [jobsData, setJobsData] = useState([]);

    // API call to get all jobs using the same format as JobsPage
    const getAllJobs = async () => {
        try {
            setLoading(true);
            const url = new URL(API_BASE_URL + API_ENDPOINTS.JOBSLIST);

            const response = await fetch(url, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                },
            });

            if (!response.ok)
                throw new Error(`HTTP error! status: ${response.status}`);

            const res = await response.json();
            console.log(`Response from API:`, res.data);

            if (res.data && Array.isArray(res.data)) {
                const formattedJobs = res.data.map((row) => ({
                    id: row.job_id,
                    jobNumber: `${row.job_code}/${row.job_id}`,
                    customerPO: row.customer_po || 'N/A',
                    description: row.job_desc || 'No description',
                    type: row.job_type || 'General',
                    status: row.status === 1 ? "Active" : "Inactive",
                    statusCode: row.status,
                    statusName: row.rt_status_master?.status_name || 'Unknown',
                    statusMasterCode: row.rt_status_master?.status_code || '',
                    priority: row.priority || 'Medium',
                    tallyNo: row.tally_no || 'N/A',
                    createdAt: row.created_at,
                    updatedAt: row.updated_at,
                    createdBy: row.created_by,
                    updatedBy: row.updated_by,
                    jobCreateDate: row.job_create_date,
                    jobEndDate: row.job_end_date,
                    jobActualStartDate: row.job_actual_start_date,
                    jobActualEndDate: row.job_actual_end_date,
                    totalDistance: parseInt(row.total_distance) || 0,
                    totalLegs: row.total_legs || 0,
                    totalDuration: parseInt(row.total_duration) || 0,
                }));

                setJobsData(formattedJobs);
                generateReportDataFromJobs(formattedJobs);
            } else {
                console.error("Invalid job list format:", res.data);
                setJobsData([]);
            }
        } catch (error) {
            console.error('Error fetching jobs:', error);
            setJobsData([]);
        } finally {
            setLoading(false);
        }
    };

    // Generate report data from actual jobs
    const generateReportDataFromJobs = (jobs) => {
        const filteredJobs = filterJobsByDateRange(jobs);

        // Basic counts
        const totalJobs = filteredJobs.length;
        const activeJobs = filteredJobs.filter(job => job.statusCode === 1).length;
        const inactiveJobs = filteredJobs.filter(job => job.statusCode === 0).length;

        // Status-based calculations (using actual status names from API)
        const completedJobs = filteredJobs.filter(job =>
            job.jobActualEndDate !== null ||
            job.statusName.toLowerCase().includes('completed') ||
            job.statusName.toLowerCase().includes('delivered')
        ).length;

        const inTransitJobs = filteredJobs.filter(job =>
            job.jobActualStartDate !== null && job.jobActualEndDate === null ||
            job.statusName.toLowerCase().includes('transit') ||
            job.statusName.toLowerCase().includes('started')
        ).length;

        const createdJobs = filteredJobs.filter(job =>
            job.statusName.toLowerCase().includes('created') ||
            job.statusName.toLowerCase().includes('assigned')
        ).length;

        // Priority distribution
        const highPriorityJobs = filteredJobs.filter(job =>
            job.priority?.toLowerCase() === 'high'
        ).length;

        const mediumPriorityJobs = filteredJobs.filter(job =>
            job.priority?.toLowerCase() === 'medium'
        ).length;

        const lowPriorityJobs = filteredJobs.filter(job =>
            job.priority?.toLowerCase() === 'low'
        ).length;

        // Job type distribution
        const jobTypeDistribution = filteredJobs.reduce((acc, job) => {
            const type = job.type || 'General';
            acc[type] = (acc[type] || 0) + 1;
            return acc;
        }, {});

        // Distance and duration calculations
        const totalDistance = filteredJobs.reduce((sum, job) => sum + job.totalDistance, 0);
        const totalDuration = filteredJobs.reduce((sum, job) => sum + job.totalDuration, 0);
        const avgDistance = totalJobs > 0 ? Math.round(totalDistance / totalJobs) : 0;
        const avgDuration = totalJobs > 0 ? Math.round((totalDuration / totalJobs) * 10) / 10 : 0;

        const delayedJobs = filteredJobs.filter(job => isJobDelayed(job)).length;

        // Unique creators (drivers/operators)
        const uniqueCreators = [...new Set(filteredJobs.map(job => job.createdBy))].length;

        setReportData({
            totalJobs,
            activeJobs,
            inactiveJobs,
            completedJobs,
            inTransitJobs,
            createdJobs,
            highPriorityJobs,
            mediumPriorityJobs,
            lowPriorityJobs,
            jobTypeDistribution,
            totalDistance,
            totalDuration,
            avgDistance,
            avgDuration,
            delayedJobs,
            uniqueCreators,
            vehicleUtilization: 85,
            customerSatisfaction: 92,
        });
    };

    // Excel Export Function
    const exportToExcel = async () => {
        try {
            setExporting(true);

            // Get filtered jobs data
            const filteredJobs = filterJobsByDateRange(jobsData);

            // Create workbook
            const workbook = XLSX.utils.book_new();

            // 1. Executive Summary Sheet
            const executiveSummaryData = [
                ['REPORTS - EXECUTIVE SUMMARY'],
                [''],
                ['Report Period:', formatDateRange()],
                ['Generated On:', new Date().toLocaleString()],
                ['Total Jobs Analyzed:', filteredJobs.length],
                [''],
                ['KEY PERFORMANCE INDICATORS'],
                ['Metric', 'Value', 'Target', 'Performance'],
                ['Total Jobs', reportData.totalJobs || 0, '100', `${((reportData.totalJobs || 0) / 100 * 100).toFixed(1)}%`],
                ['Completed Jobs', reportData.completedJobs || 0, reportData.totalJobs || 1, `${(((reportData.completedJobs || 0) / Math.max(reportData.totalJobs || 1, 1)) * 100).toFixed(1)}%`],
                ['In Transit Jobs', reportData.inTransitJobs || 0, Math.ceil((reportData.totalJobs || 0) * 0.2), `${(((reportData.inTransitJobs || 0) / Math.max(Math.ceil((reportData.totalJobs || 0) * 0.2), 1)) * 100).toFixed(1)}%`],
                ['Total Distance Covered', `${(reportData.totalDistance || 0).toLocaleString()} km`, '', ''],
                ['Average Distance per Job', `${reportData.avgDistance || 0} km`, '300 km', reportData.avgDistance >= 300 ? 'Above Target' : 'Below Target'],
                ['Active Operators', reportData.uniqueCreators || 0, '', ''],
                [''],
                ['STATUS DISTRIBUTION'],
                ['Active Jobs', reportData.activeJobs || 0, '', `${(((reportData.activeJobs || 0) / Math.max(reportData.totalJobs || 1, 1)) * 100).toFixed(1)}%`],
                ['Inactive Jobs', reportData.inactiveJobs || 0, '', `${(((reportData.inactiveJobs || 0) / Math.max(reportData.totalJobs || 1, 1)) * 100).toFixed(1)}%`],
                ['Completed Jobs', reportData.completedJobs || 0, '', `${(((reportData.completedJobs || 0) / Math.max(reportData.totalJobs || 1, 1)) * 100).toFixed(1)}%`],
                ['In Transit Jobs', reportData.inTransitJobs || 0, '', `${(((reportData.inTransitJobs || 0) / Math.max(reportData.totalJobs || 1, 1)) * 100).toFixed(1)}%`],
                ['Created Jobs', reportData.createdJobs || 0, '', `${(((reportData.createdJobs || 0) / Math.max(reportData.totalJobs || 1, 1)) * 100).toFixed(1)}%`],
            ];

            const summarySheet = XLSX.utils.aoa_to_sheet(executiveSummaryData);
            summarySheet['!cols'] = [
                { width: 30 },
                { width: 20 },
                { width: 15 },
                { width: 20 }
            ];

            XLSX.utils.book_append_sheet(workbook, summarySheet, 'Executive Summary');

            // 2. Detailed Jobs Report Sheet
            if (filteredJobs.length > 0) {
                const jobsForExport = filteredJobs.map(job => ({
                    'Job ID': job.id,
                    'Job Number': job.jobNumber,
                    'Customer PO': job.customerPO,
                    'Job Type': job.type,
                    'Status': job.status,
                    'Status Name': job.statusName,
                    'Priority': job.priority,
                    'Tally Number': job.tallyNo,
                    'Description': job.description,
                    'Total Distance (km)': job.totalDistance,
                    'Total Duration (hrs)': job.totalDuration,
                    'Total Legs': job.totalLegs,
                    'Created By': job.createdBy || 'N/A',
                    'Created At': job.createdAt ? new Date(job.createdAt).toLocaleString() : 'N/A',
                    'Updated At': job.updatedAt ? new Date(job.updatedAt).toLocaleString() : 'N/A',
                    'Job Create Date': job.jobCreateDate ? new Date(job.jobCreateDate).toLocaleDateString() : 'N/A',
                    'Planned End Date': job.jobEndDate ? new Date(job.jobEndDate).toLocaleDateString() : 'N/A',
                    'Actual Start Date': job.jobActualStartDate ? new Date(job.jobActualStartDate).toLocaleDateString() : 'Not Started',
                    'Actual End Date': job.jobActualEndDate ? new Date(job.jobActualEndDate).toLocaleDateString() : 'Not Completed',
                    'Is Delayed': isJobDelayed(job) ? 'Yes' : 'No'
                }));

                const jobsSheet = XLSX.utils.json_to_sheet(jobsForExport);

                // Auto-width columns for jobs sheet
                const jobsColumns = Object.keys(jobsForExport[0]).map(key => ({
                    width: Math.max(key.length + 2, 12)
                }));
                jobsSheet['!cols'] = jobsColumns;

                XLSX.utils.book_append_sheet(workbook, jobsSheet, 'Detailed Jobs Report');
            }

            // 3. Priority Analysis Sheet
            const priorityData = [
                ['PRIORITY ANALYSIS'],
                [''],
                ['Priority Level', 'Count', 'Percentage', 'Avg Distance', 'Avg Duration', 'Status Distribution'],
                ['High Priority', reportData.highPriorityJobs || 0, `${(((reportData.highPriorityJobs || 0) / Math.max(reportData.totalJobs || 1, 1)) * 100).toFixed(1)}%`,
                    calculateAvgByPriority(filteredJobs, 'high', 'distance'), calculateAvgByPriority(filteredJobs, 'high', 'duration'),
                    getStatusDistributionByPriority(filteredJobs, 'high')],
                ['Medium Priority', reportData.mediumPriorityJobs || 0, `${(((reportData.mediumPriorityJobs || 0) / Math.max(reportData.totalJobs || 1, 1)) * 100).toFixed(1)}%`,
                    calculateAvgByPriority(filteredJobs, 'medium', 'distance'), calculateAvgByPriority(filteredJobs, 'medium', 'duration'),
                    getStatusDistributionByPriority(filteredJobs, 'medium')],
                ['Low Priority', reportData.lowPriorityJobs || 0, `${(((reportData.lowPriorityJobs || 0) / Math.max(reportData.totalJobs || 1, 1)) * 100).toFixed(1)}%`,
                    calculateAvgByPriority(filteredJobs, 'low', 'distance'), calculateAvgByPriority(filteredJobs, 'low', 'duration'),
                    getStatusDistributionByPriority(filteredJobs, 'low')],
                [''],
                ['JOB TYPE ANALYSIS'],
                ['Job Type', 'Count', 'Percentage', 'Total Distance', 'Avg Distance'],
                ...Object.entries(reportData.jobTypeDistribution || {}).map(([type, count]) => {
                    const typeJobs = filteredJobs.filter(job => job.type === type);
                    const totalDist = typeJobs.reduce((sum, job) => sum + job.totalDistance, 0);
                    const avgDist = count > 0 ? Math.round(totalDist / count) : 0;
                    return [
                        type,
                        count,
                        `${((count / Math.max(reportData.totalJobs || 1, 1)) * 100).toFixed(1)}%`,
                        `${totalDist.toLocaleString()} km`,
                        `${avgDist} km`
                    ];
                })
            ];

            const prioritySheet = XLSX.utils.aoa_to_sheet(priorityData);
            prioritySheet['!cols'] = [
                { width: 20 },
                { width: 10 },
                { width: 12 },
                { width: 15 },
                { width: 15 },
                { width: 20 }
            ];

            XLSX.utils.book_append_sheet(workbook, prioritySheet, 'Priority & Type Analysis');

            // 5. Monthly Trends Sheet (if data spans multiple months)
            const trendsData = generateTrendsData(filteredJobs);
            if (trendsData.length > 0) {
                const trendsSheet = XLSX.utils.json_to_sheet(trendsData);
                trendsSheet['!cols'] = [
                    { width: 15 },
                    { width: 12 },
                    { width: 12 },
                    { width: 15 },
                    { width: 15 },
                    { width: 15 },
                    { width: 18 }
                ];

                XLSX.utils.book_append_sheet(workbook, trendsSheet, 'Trends Analysis');
            }

            // Generate filename with date range
            const filename = `Logistics_Report_${dateRange.start}_to_${dateRange.end}.xlsx`;

            // Write and download the file
            XLSX.writeFile(workbook, filename);

            console.log('Excel report exported successfully!');

        } catch (error) {
            console.error('Error exporting to Excel:', error);
        } finally {
            setExporting(false);
        }
    };

    // Helper functions for Excel export
    const calculateAvgByPriority = (jobs, priority, metric) => {
        const priorityJobs = jobs.filter(job => job.priority?.toLowerCase() === priority);
        if (priorityJobs.length === 0) return '0';

        const total = priorityJobs.reduce((sum, job) =>
            sum + (metric === 'distance' ? job.totalDistance : job.totalDuration), 0
        );

        const avg = Math.round(total / priorityJobs.length);
        return metric === 'distance' ? `${avg} km` : `${avg} hrs`;
    };

    const getStatusDistributionByPriority = (jobs, priority) => {
        const priorityJobs = jobs.filter(job => job.priority?.toLowerCase() === priority);
        const completed = priorityJobs.filter(job => job.jobActualEndDate).length;
        const inTransit = priorityJobs.filter(job => job.jobActualStartDate && !job.jobActualEndDate).length;
        const created = priorityJobs.length - completed - inTransit;

        return `C:${completed}, T:${inTransit}, N:${created}`;
    };

    const generateTrendsData = (jobs) => {
        // Group jobs by month for trend analysis
        const monthlyData = {};

        jobs.forEach(job => {
            const date = new Date(job.createdAt);
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

            if (!monthlyData[monthKey]) {
                monthlyData[monthKey] = {
                    month: monthKey,
                    totalJobs: 0,
                    completedJobs: 0,
                    totalDistance: 0,
                    totalRevenue: 0,
                    avgDistance: 0,
                    onTimeRate: 0
                };
            }

            monthlyData[monthKey].totalJobs++;
            if (job.jobActualEndDate) monthlyData[monthKey].completedJobs++;
            monthlyData[monthKey].totalDistance += job.totalDistance;
        });

        // Calculate averages and format data
        return Object.values(monthlyData).map(month => ({
            'Month': month.month,
            'Total Jobs': month.totalJobs,
            'Completed Jobs': month.completedJobs,
            'Total Distance (km)': month.totalDistance.toLocaleString(),
            'Average Distance (km)': Math.round(month.totalDistance / month.totalJobs)
        }));
    };

    // Filter jobs by date range
    const filterJobsByDateRange = (jobs) => {
        const startDate = new Date(dateRange.start);
        const endDate = new Date(dateRange.end);
        endDate.setHours(23, 59, 59, 999);

        return jobs.filter(job => {
            const jobDate = new Date(job.createdAt);
            return jobDate >= startDate && jobDate <= endDate;
        });
    };

    // Check if job is delayed
    const isJobDelayed = (job) => {
        if (!job.jobEndDate || !job.jobActualEndDate) return false;
        return new Date(job.jobActualEndDate) > new Date(job.jobEndDate);
    };

    // Format jobs for display in table
    const formatJobsForDisplay = () => {
        const filteredJobs = filterJobsByDateRange(jobsData);

        return filteredJobs.slice(0, 15).map(job => ({
            id: job.id,
            jobNumber: job.jobNumber,
            customerPO: job.customerPO,
            description: job.description,
            type: job.type,
            status: job.statusName,
            priority: job.priority,
            tallyNo: job.tallyNo,
            createdAt: job.createdAt,
            createdBy: job.createdBy,
            duration: job.totalDuration,
            totalLegs: job.totalLegs,
            onTime: !isJobDelayed(job),
            actualStartDate: job.jobActualStartDate,
            actualEndDate: job.jobActualEndDate,
            plannedEndDate: job.jobEndDate
        }));
    };

    // Load data on component mount and date range change
    useEffect(() => {
        getAllJobs();
    }, []);

    useEffect(() => {
        if (jobsData.length > 0) {
            generateReportDataFromJobs(jobsData);
        }
    }, [dateRange, jobsData]);

    const handleDateRangeChange = (field, value) => {
        const today = new Date().toISOString().split('T')[0];
        if (value > today) return;

        setDateRange(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const setQuickDateRange = (days) => {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - days + 1);

        setDateRange({
            start: startDate.toISOString().split('T')[0],
            end: endDate.toISOString().split('T')[0]
        });
    };

    const formatDateRange = () => {
        const start = new Date(dateRange.start);
        const end = new Date(dateRange.end);

        if (dateRange.start === dateRange.end) {
            return start.toLocaleDateString('en-IN', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
            });
        }

        return `${start.toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short'
        })} - ${end.toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        })}`;
    };

    // Utility functions
    const getStatusConfig = (status) => {
        const config = JOB_STATUSES[status.toUpperCase()];
        return (
            config || {
                color: "gray",
                label: status,
                bgColor: "bg-gray-100",
                textColor: "text-gray-800",
            }
        );
    };

    const getPriorityConfig = (priority) => {
        if (!priority) return { color: "gray", label: "Medium", icon: Star };

        const configs = {
            urgent: { color: "red", label: "Urgent", icon: AlertTriangle },
            high: { color: "red", label: "High", icon: Flag },
            medium: { color: "yellow", label: "Medium", icon: Star },
            low: { color: "green", label: "Low", icon: CheckCircle },
        };
        return configs[priority.toLowerCase()] || configs.medium;
    };

    // Job status and type configurations
    const JOB_STATUSES = {
        ACTIVE: { value: '1', label: 'Active', color: 'success', bgColor: 'bg-green-100', textColor: 'text-green-800' },
        INACTIVE: { value: '0', label: 'Inactive', color: 'danger', bgColor: 'bg-red-100', textColor: 'text-red-800' },
        ASSIGNED: { value: 'assigned', label: 'Assigned', color: 'blue', bgColor: 'bg-blue-100', textColor: 'text-blue-800' },
        IN_TRANSIT: { value: 'in_transit', label: 'In Transit', color: 'warning', bgColor: 'bg-yellow-100', textColor: 'text-yellow-800' },
        COMPLETED: { value: 'completed', label: 'Completed', color: 'success', bgColor: 'bg-green-100', textColor: 'text-green-800' },
        CANCELLED: { value: 'cancelled', label: 'Cancelled', color: 'danger', bgColor: 'bg-red-100', textColor: 'text-red-800' }
    };

    const JOB_TYPES = {
        import: {
            value: "import",
            label: "Import",
            icon: Ship,
        },
        export: {
            value: "export",
            label: "Export",
            icon: Send,
        },
        domestic: {
            value: "domestic",
            label: "Domestic",
            icon: Home,
        },
        vendor: {
            value: "vendor",
            label: "Vendor",
            icon: Store,
        },
        customer: {
            value: "customer",
            label: "Customer",
            icon: User,
        },
        GENERAL: {
            value: "General",
            label: "General Transport",
            icon: Truck,
        }
    };

    const DateRangePicker = () => (
        <div className="relative">
            <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDatePicker(!showDatePicker)}
                className="flex items-center space-x-2"
            >
                <CalendarDays className="w-4 h-4" />
                <span>{formatDateRange()}</span>
            </Button>

            {showDatePicker && (
                <div className="absolute right-0 top-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-50 w-80">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-semibold text-gray-900">Select Date Range</h3>
                        <Button variant="ghost" size="xs" onClick={() => setShowDatePicker(false)}>
                            <X className="w-4 h-4" />
                        </Button>
                    </div>

                    <div className="space-y-4">
                        <div className="grid grid-cols-3 gap-2">
                            <Button variant="outline" size="xs" onClick={() => setQuickDateRange(1)} className="text-xs">
                                Today
                            </Button>
                            <Button variant="outline" size="xs" onClick={() => setQuickDateRange(7)} className="text-xs">
                                7 Days
                            </Button>
                            <Button variant="outline" size="xs" onClick={() => setQuickDateRange(30)} className="text-xs">
                                30 Days
                            </Button>
                        </div>

                        <div className="space-y-3">
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-2">Start Date</label>
                                <input
                                    type="date"
                                    value={dateRange.start}
                                    max={new Date().toISOString().split('T')[0]}
                                    onChange={(e) => handleDateRangeChange('start', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-2">End Date</label>
                                <input
                                    type="date"
                                    value={dateRange.end}
                                    min={dateRange.start}
                                    max={new Date().toISOString().split('T')[0]}
                                    onChange={(e) => handleDateRangeChange('end', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                />
                            </div>
                        </div>

                        <div className="flex justify-end pt-3 border-t">
                            <Button size="sm" onClick={() => setShowDatePicker(false)}>
                                Apply
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    const displayJobs = formatJobsForDisplay();

    return (
        <div className="p-6 space-y-6">
            {/* Page Header */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                        <BarChart3 className="w-6 h-6 mr-2 text-blue-600" />
                        Logistics Reports Dashboard
                    </h1>
                    <p className="text-gray-600 mt-1">
                        Real-time analytics and performance insights
                    </p>
                </div>

                <div className="flex items-center space-x-3 mt-4 lg:mt-0">
                    {activeTab === 'overview' && (
                        <>
                            <DateRangePicker />
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={exportToExcel}
                                loading={exporting}
                                className="flex items-center space-x-2 hover:bg-green-50 hover:border-green-300"
                            >
                                <FileSpreadsheet className="w-4 h-4" />
                                <span>Export Report</span>
                            </Button>
                            <Button variant="outline" size="sm" onClick={getAllJobs} loading={loading}>
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Refresh
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {/* Tabs Navigation */}
            <Card className="p-2">
                <div className="flex space-x-2">
                    <Button
                        variant={activeTab === 'overview' ? 'primary' : 'ghost'}
                        size="sm"
                        onClick={() => setActiveTab('overview')}
                        className="flex items-center space-x-2"
                    >
                        <BarChart3 className="w-4 h-4" />
                        <span>Overview</span>
                    </Button>
                    <Button
                        variant={activeTab === 'route-analytics' ? 'primary' : 'ghost'}
                        size="sm"
                        onClick={() => setActiveTab('route-analytics')}
                        className="flex items-center space-x-2"
                    >
                        <Navigation className="w-4 h-4" />
                        <span>Route Analytics</span>
                    </Button>
                </div>
            </Card>

            {/* Tab Content */}
            {activeTab === 'overview' && (
                <>
                    {/* Key Performance Indicators */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
                <Card className="p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Total Jobs</p>
                            <p className="text-3xl font-bold text-gray-900">{reportData.totalJobs || 0}</p>
                            <p className="text-xs text-gray-500 mt-1">
                                {reportData.activeJobs || 0} active, {reportData.inactiveJobs || 0} inactive
                            </p>
                        </div>
                        <div className="p-3 rounded-lg bg-blue-100">
                            <Package className="w-8 h-8 text-blue-600" />
                        </div>
                    </div>
                </Card>

                <Card className="p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Distance Covered</p>
                            <p className="text-3xl font-bold text-gray-900">{(reportData.totalDistance || 0).toLocaleString()}</p>
                            <p className="text-xs text-gray-500 mt-1">
                                Avg: {reportData.avgDistance || 0} km per job
                            </p>
                        </div>
                        <div className="p-3 rounded-lg bg-purple-100">
                            <Truck className="w-8 h-8 text-purple-600" />
                        </div>
                    </div>
                </Card>
            </div>

            {/* Status Overview */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                    <div className="p-6 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                            <Activity className="w-5 h-5 mr-2 text-blue-600" />
                            Job Status Distribution
                        </h3>
                    </div>
                    <div className="p-6">
                        <div className="grid grid-cols-3 gap-4">
                            <div className="text-center p-4 bg-green-50 rounded-lg">
                                <div className="text-2xl font-bold text-green-600">{reportData.completedJobs || 0}</div>
                                <div className="text-sm text-gray-600 mt-1">Completed</div>
                            </div>
                            <div className="text-center p-4 bg-yellow-50 rounded-lg">
                                <div className="text-2xl font-bold text-yellow-600">{reportData.inTransitJobs || 0}</div>
                                <div className="text-sm text-gray-600 mt-1">In Transit</div>
                            </div>
                            <div className="text-center p-4 bg-blue-50 rounded-lg">
                                <div className="text-2xl font-bold text-blue-600">{reportData.createdJobs || 0}</div>
                                <div className="text-sm text-gray-600 mt-1">Created</div>
                            </div>
                        </div>
                    </div>
                </Card>

                <Card>
                    <div className="p-6 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                            <Flag className="w-5 h-5 mr-2 text-red-600" />
                            Priority Distribution
                        </h3>
                    </div>
                    <div className="p-6">
                        <div className="grid grid-cols-3 gap-4">
                            <div className="text-center p-4 bg-red-50 rounded-lg">
                                <div className="text-2xl font-bold text-red-600">{reportData.highPriorityJobs || 0}</div>
                                <div className="text-sm text-gray-600 mt-1">High Priority</div>
                            </div>
                            <div className="text-center p-4 bg-yellow-50 rounded-lg">
                                <div className="text-2xl font-bold text-yellow-600">{reportData.mediumPriorityJobs || 0}</div>
                                <div className="text-sm text-gray-600 mt-1">Medium Priority</div>
                            </div>
                            <div className="text-center p-4 bg-green-50 rounded-lg">
                                <div className="text-2xl font-bold text-green-600">{reportData.lowPriorityJobs || 0}</div>
                                <div className="text-sm text-gray-600 mt-1">Low Priority</div>
                            </div>
                        </div>
                    </div>
                </Card>
            </div>

            {/* Job Type Analysis */}
            <Card>
                <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                            <Truck className="w-5 h-5 mr-2 text-blue-600" />
                            Job Type Analysis
                        </h3>
                        <Badge variant="outline" className="text-xs">
                            {Object.keys(reportData.jobTypeDistribution || {}).length} Types
                        </Badge>
                    </div>
                </div>
                <div className="p-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                        {Object.entries(reportData.jobTypeDistribution || {}).map(([type, count]) => (
                            <div key={type} className="text-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                <div className="text-xl font-bold text-gray-900">{count}</div>
                                <div className="text-sm text-gray-600 mt-1">{type}</div>
                                <div className="text-xs text-gray-500 mt-1">
                                    {reportData.totalJobs > 0 ?
                                        `${Math.round((count / reportData.totalJobs) * 100)}%` : '0%'
                                    }
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </Card>

            {/* Recent Jobs Table */}
            <Card>
                <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                            <Package className="w-5 h-5 mr-2 text-blue-600" />
                            Recent Jobs Overview
                        </h3>
                        <div className="flex items-center space-x-2">
                            <Badge variant="outline">
                                Showing {Math.min(15, displayJobs.length)} of {filterJobsByDateRange(jobsData).length}
                            </Badge>
                            <Button variant="outline" size="sm" onClick={() => navigate('/jobs')}>
                                View All Jobs
                            </Button>
                        </div>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Job Details
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status & Priority
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Information
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Timeline
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {displayJobs.map((job, jobIndex) => {
                                const statusConfig = getStatusConfig(job.status);
                                const priorityConfig = getPriorityConfig(job.priority);
                                const PriorityIcon = priorityConfig.icon;
                                const TypeIcon = JOB_TYPES[job.type]?.icon || Package;

                                return (
                                    <tr key={job.id} className="hover:bg-gray-50">

                                        {/* Job Details */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                                    <TypeIcon className="w-5 h-5 text-blue-600" />
                                                </div>
                                                <div className="ml-3">
                                                    <div className="flex items-center">
                                                        <button
                                                            onClick={() => navigate(`/jobs/${job.id}`)}
                                                            className="font-medium text-blue-600 hover:text-blue-800"
                                                        >
                                                            {job.jobNumber}
                                                        </button>
                                                    </div>
                                                    <p className="text-sm text-gray-500">
                                                        {JOB_TYPES[job.type]?.label || job.type}
                                                    </p>
                                                    <p className="text-xs text-gray-400">PO: {job.customerPO}</p>
                                                </div>
                                            </div>
                                        </td>

                                        {/* Status & Priority */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="space-y-2">
                                                <Badge
                                                    variant={statusConfig.color}
                                                    className={`${statusConfig.bgColor} ${statusConfig.textColor}`}
                                                >
                                                    {job.status}
                                                </Badge>
                                                {job.priority && (
                                                    <div className="flex items-center">
                                                        <Badge variant={priorityConfig.color} size="sm">
                                                            <PriorityIcon className="w-3 h-3 mr-1" />
                                                            {priorityConfig.label}
                                                        </Badge>
                                                    </div>
                                                )}
                                            </div>
                                        </td>

                                        {/* Information */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="space-y-1">
                                                <div className="text-sm text-gray-900">
                                                    <Hash className="w-3 h-3 inline mr-1" />
                                                    Tally: {job.tallyNo}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    Created by: {job.createdBy}
                                                </div>
                                                <div className="text-xs text-gray-500 truncate max-w-xs">
                                                    {job.description}
                                                </div>
                                                {job.totalDistance && (
                                                    <div className="text-xs text-gray-500">
                                                        <MapPin className="w-3 h-3 inline mr-1" />
                                                        {job.totalDistance} km
                                                    </div>
                                                )}
                                            </div>
                                        </td>

                                        {/* Timeline */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="space-y-1">
                                                <div className="text-xs text-gray-500">
                                                    <Calendar className="w-3 h-3 inline mr-1" />
                                                    Created: {new Date(job.createdAt).toLocaleDateString()}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    {new Date(job.createdAt).toLocaleTimeString([], {
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                    })}
                                                </div>
                                                {job.jobCreateDate && (
                                                    <div className="text-xs text-gray-500">
                                                        <Clock className="w-3 h-3 inline mr-1" />
                                                        Start: {new Date(job.jobCreateDate).toLocaleDateString()}
                                                    </div>
                                                )}
                                                {job.jobEndDate && (
                                                    <div className="text-xs text-gray-500">
                                                        End: {new Date(job.jobEndDate).toLocaleDateString()}
                                                    </div>
                                                )}
                                            </div>
                                        </td>

                                        {/* Actions */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center space-x-1">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => navigate(`/jobs/${job.id}`)}
                                                    title="View Details"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {displayJobs.length === 0 && !loading && (
                    <div className="text-center py-8">
                        <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500 mb-4">No jobs found for the selected date range</p>
                        <Button
                            variant="outline"
                            onClick={exportToExcel}
                            loading={exporting}
                            size="sm"
                        >
                            <FileSpreadsheet className="w-4 h-4 mr-2" />
                            Export Empty Report
                        </Button>
                    </div>
                )}

                {loading && (
                    <div className="text-center py-8">
                        <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
                        <p className="text-gray-500">Loading jobs data...</p>
                    </div>
                )}
            </Card>
                </>
            )}

            {/* Route Analytics Tab */}
            {activeTab === 'route-analytics' && <RouteAnalyticsTab />}
        </div>
    );
};

export default ReportsPage;